import { generatePath } from 'react-router';

import {
  FILTER_PARAM_PREFIX,
  FILTER_PROMPT_DEPLOYMENT_ID_KEY,
  FILTER_PROMPT_DEPLOYMENT_KEY,
  FILTER_PROMPT_DEPLOYMENT_SEGMENT_JOINER,
  FILTER_PROMPT_DEPLOY_COUNT_KEY,
  FILTER_PROMPT_DEPLOY_ROLLBACK_KEY,
  FILTER_PROMPT_HEADER_TEXT_KEY,
  FILTER_PROMPT_QUERY_KEY,
  FILTER_PROMPT_TRIGGER_TEXT_KEY,
  FILTER_PROMPT_VALUES_KEY,
  routes,
} from '@fly/shared';

const REGISTRY_RELEASES_WORKFLOW_TAB = 'all_releases' as const;

export type DeploymentWaveRef = {
  environmentName: string;
  workflowName: string;
  workflowNumber: string;
};

export function buildDeploymentWaveRegistryReleasesHref(
  repositoryOwner: string,
  repositoryName: string,
  options: {
    /** Server-provided display name (deployment title or workflow label); not i18n. */
    displayName: string;
    /** Wave size for locale-safe prompt UI on the Releases page. */
    deployCount: number;
    isRollback?: boolean;
    promptValues?: string;
    /** Opaque `DeploymentDelta.id`; preferred over `deployment` triple in URL. */
    deploymentId?: string;
    deployment?: DeploymentWaveRef;
    /**
     * Legacy deep links that embed locale-resolved prompt strings in the URL.
     * Prefer `displayName` + `deployCount` + `isRollback` for new links.
     */
    promptQuery?: string;
    triggerText?: string;
    headerText?: string;
  }
): string {
  const pathname = generatePath(routes.DASHBOARD.REGISTRY.REPOSITORY.RELEASES, {
    owner: repositoryOwner,
    name: repositoryName,
  });
  const search = new URLSearchParams({
    workflow: REGISTRY_RELEASES_WORKFLOW_TAB,
  });

  const displayName = options.displayName.trim();
  if (displayName) {
    search.set(
      `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_HEADER_TEXT_KEY}`,
      displayName
    );
  }
  search.set(
    `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_DEPLOY_COUNT_KEY}`,
    String(options.deployCount)
  );
  if (options.isRollback) {
    search.set(
      `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_DEPLOY_ROLLBACK_KEY}`,
      '1'
    );
  }

  if (options.promptQuery) {
    search.set(
      `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_QUERY_KEY}`,
      options.promptQuery
    );
  }
  if (options.promptValues) {
    search.set(
      `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_VALUES_KEY}`,
      options.promptValues
    );
  }
  const triggerText = options.triggerText?.trim();
  if (triggerText) {
    search.set(
      `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_TRIGGER_TEXT_KEY}`,
      triggerText
    );
  }
  const legacyHeaderText = options.headerText?.trim();
  if (legacyHeaderText && legacyHeaderText !== displayName) {
    search.set(
      `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_HEADER_TEXT_KEY}`,
      legacyHeaderText
    );
  }
  const deploymentId = options.deploymentId?.trim();
  if (deploymentId) {
    search.set(
      `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_DEPLOYMENT_ID_KEY}`,
      deploymentId
    );
  } else if (options.deployment) {
    const triple = [
      encodeURIComponent(options.deployment.environmentName),
      encodeURIComponent(options.deployment.workflowName),
      encodeURIComponent(options.deployment.workflowNumber),
    ].join(FILTER_PROMPT_DEPLOYMENT_SEGMENT_JOINER);
    search.set(`${FILTER_PARAM_PREFIX}${FILTER_PROMPT_DEPLOYMENT_KEY}`, triple);
  }
  return `${pathname}?${search.toString()}`;
}
